import React from 'react';

import { Box, Button, IconButton, Stack, Typography, TypographySystem } from '@mui/joy';
import { useColorScheme } from '@mui/joy/styles';
import { MdClose } from 'react-icons/md';

import expertChatPreviewDark from '../../../assets/images/expert-chat-preview-dark.png';
import expertChatPreview from '../../../assets/images/expert-chat-preview.png';
import meetingMessagePreviewDark from '../../../assets/images/meeting-message-preview-dark.png';
import meetingMessagePreview from '../../../assets/images/meeting-message-preview.png';

const ExpertChatPreview: React.FC = () => {
  const { mode } = useColorScheme();
  const src = mode === 'dark' ? expertChatPreviewDark : expertChatPreview;
  return (
    <Box
      component='img'
      src={src}
      alt='Example: a question to a Calyx expert and their answer'
      sx={{
        width: '100%',
        aspectRatio: '680 / 376',
        display: 'block',
        borderRadius: '20px',
      }}
    />
  );
};

const MeetingMessagePreview: React.FC = () => {
  const { mode } = useColorScheme();
  const src = mode === 'dark' ? meetingMessagePreviewDark : meetingMessagePreview;
  return (
    <Box
      component='img'
      src={src}
      alt='Example: a scheduled methodology overview with a Calyx REDD lead'
      sx={{
        width: '100%',
        aspectRatio: '680 / 174',
        display: 'block',
        borderRadius: '20px',
      }}
    />
  );
};

type Props = {
  title: string;
  body: string;
  customContent?: React.ReactNode;
  stepIndex: number;
  totalSteps: number;
  interactive?: boolean;
  onBack: () => void;
  onContinue: () => void;
  onClose: () => void;
};

export const HomeOnboardingPopover: React.FC<Props> = ({
  title,
  body,
  customContent,
  stepIndex,
  totalSteps,
  interactive = false,
  onBack,
  onContinue,
  onClose,
}) => {
  const isLast = stepIndex === totalSteps - 1;

  return (
    <Stack
      sx={(t) => ({
        bgcolor: t.palette.background.surface,
        boxShadow: 'lg',
        borderRadius: '10px',
        p: '20px',
        gap: 2,
        width: 360,
        boxSizing: 'border-box',
      })}
    >
      <Stack
        direction='row'
        alignItems='flex-start'
        justifyContent='space-between'
        sx={{ width: '100%' }}
      >
        <Typography
          level={'sub1' as keyof TypographySystem}
          sx={(t) => ({
            flex: 1,
            minWidth: 0,
            color: t.palette.text.primary,
          })}
        >
          {title}
        </Typography>
        <IconButton
          variant='plain'
          size='sm'
          onClick={onClose}
          aria-label='Close tour'
          sx={(t) => ({ color: t.palette.text.icon })}
        >
          <MdClose size={24} />
        </IconButton>
      </Stack>

      {customContent}

      <Typography
        level='body-md'
        sx={(t) => ({
          color: t.palette.text.primary,
          width: '100%',
        })}
      >
        {body}
      </Typography>

      <Stack
        direction='row'
        justifyContent='space-between'
        alignItems='center'
        sx={{ width: '100%' }}
      >
        <Typography level='body-md' sx={(t) => ({ color: t.palette.text.secondary })}>
          Step {stepIndex + 1} of {totalSteps}
        </Typography>
        <Stack direction='row' gap={1.5} alignItems='center'>
          {/* Always enabled so the secondary-CTA analytics event fires on every
              screen (incl. the first); onBack no-ops navigation when idx===0. */}
          <Button variant='outlined' color='neutral' size='sm' onClick={onBack}>
            Back
          </Button>
          {!interactive && (
            <Button variant='solid' color='primary' size='sm' onClick={onContinue}>
              {isLast ? 'Finish' : 'Continue'}
            </Button>
          )}
        </Stack>
      </Stack>
    </Stack>
  );
};

export { ExpertChatPreview, MeetingMessagePreview };
